import React from "react";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import ProductImage from "@/components/ProductImage";

export default function CartDrawer({ open, onClose }) {
  const { items, remove, setQty, subtotal, clear } = useCart();
  const shipRemaining = Math.max(0, 75 - subtotal);

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/60 z-[60] transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white text-black z-[70] shadow-2xl transform transition-transform duration-300 flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/10">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} />
            <div className="font-black tracking-[0.14em] text-[13px] uppercase">Your Cart</div>
          </div>
          <button onClick={onClose} className="p-1 hover:text-[#c60d0d]">
            <X size={20} />
          </button>
        </div>

        {/* Free shipping progress */}
        <div className="px-5 py-4 bg-[#faf9f7] border-b border-black/10">
          <div className="text-[11px] tracking-[0.14em] uppercase text-black/60 mb-2">
            {shipRemaining > 0
              ? `Add $${shipRemaining} for FREE shipping`
              : "🎉 You unlocked FREE shipping"}
          </div>
          <div className="h-1.5 bg-black/10 overflow-hidden">
            <div
              className="h-full bg-[#c60d0d] transition-all duration-500"
              style={{ width: `${Math.min(100, (subtotal / 75) * 100)}%` }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center px-8 text-center">
              <ShoppingBag size={40} className="text-black/20 mb-4" />
              <div className="font-black tracking-tight text-lg">Your cart is empty</div>
              <div className="text-[13px] text-black/50 mt-2">Time to stock the drawer.</div>
              <button
                onClick={onClose}
                className="mt-6 bg-[#c60d0d] hover:bg-[#e11d1d] text-white px-6 py-3 text-[12px] tracking-[0.18em] font-black uppercase transition"
              >
                Shop Now
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-black/10">
              {items.map((i) => (
                <li key={i.id} className="p-5 flex gap-4">
                  <div className="w-16 h-20 shrink-0 relative overflow-hidden bg-[#0a0a0a] flex items-center justify-center p-1">
                    <ProductImage src={i.img} alt={i.color} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-black/50">{i.color}</div>
                    <div className="text-[13px] font-bold">{i.name}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center border border-black/15">
                        <button
                          onClick={() => setQty(i.id, i.qty - 1)}
                          className="px-2 py-1 hover:bg-black/5"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-[13px] font-bold">{i.qty}</span>
                        <button
                          onClick={() => setQty(i.id, i.qty + 1)}
                          className="px-2 py-1 hover:bg-black/5"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-black text-[14px]">${i.qty * i.price}</span>
                        <button
                          onClick={() => remove(i.id)}
                          className="text-black/40 hover:text-[#c60d0d]"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-black/10 p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[12px] tracking-[0.18em] uppercase text-black/60">Subtotal</span>
              <span className="text-2xl font-black">${subtotal}</span>
            </div>
            <button className="w-full bg-[#c60d0d] hover:bg-[#e11d1d] text-white py-4 text-[13px] tracking-[0.2em] font-black uppercase transition">
              Checkout
            </button>
            <button
              onClick={clear}
              className="w-full mt-2 text-[11px] tracking-[0.16em] uppercase text-black/40 hover:text-[#c60d0d] py-2"
            >
              Clear cart
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
